"use client";

import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Plus,
  Trash2,
  Edit3,
  Type,
  Hash,
  List,
  Mail,
  Link,
  Globe,
  X,
  ChevronLeft,
  ChevronRight,
  Package,
  FolderOpen,
  Tag,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import {
  StandardFormTagInput,
  StandardFormInput,
} from "@/components/common/StandardFormInput";
import { ActionButton } from "@/components/common/DataViewTypes";
import { RootState, AppDispatch } from "@/store/store";
import {
  fetchMetafieldsRequest,
  createMetafieldRequest,
  updateMetafieldRequest,
  deleteMetafieldRequest,
  resetFormState,
  checkMetafieldKeyRequest,
  resetKeyValidation,
} from "@/store/slices/metafield.slice";
import {
  Metafield,
  CreateMetafieldDto,
  UpdateMetafieldDto,
  MetafieldType,
  MetafieldAppliedTo,
} from "@/types/models/metafield";
import { isValidSlug } from "@/utils/helper";

interface AddMetaFieldForm {
  key: string;
  type: MetafieldType | "";
  accepted_range: number[];
  accepted_values: string[];
  applied_to: MetafieldAppliedTo | "";
}

const MetaFields = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { confirm } = useConfirmation();

  const {
    items: metaFields,
    loaded,
    loading,
    error,
    pagination,
    formState,
    keyValidation,
  } = useSelector((state: RootState) => state.metafields);
  const [pageIndex, setPageIndex] = useState(0);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [addForm, setAddForm] = useState<AddMetaFieldForm>({
    key: "",
    type: "",
    accepted_range: [],
    accepted_values: [],
    applied_to: "",
  });

  const fetchMetaFields = useCallback(() => {
    dispatch(fetchMetafieldsRequest({ page: pageIndex + 1 }));
  }, [dispatch, pageIndex]);

  useEffect(() => {
    if (loading) return;

    const currentPageIndex = Math.max(0, (pagination?.page || 1) - 1);
    const hasPageMismatch = currentPageIndex !== pageIndex;

    if (!loaded && (metaFields?.length || 0) === 0) {
      fetchMetaFields();
      return;
    }

    if (loaded && hasPageMismatch) {
      fetchMetaFields();
    }
  }, [
    loaded,
    loading,
    error,
    pagination?.page,
    pageIndex,
    metaFields?.length,
    fetchMetaFields,
  ]);

  useEffect(() => {
    if (!isAddModalOpen) {
      dispatch(resetFormState());
      dispatch(resetKeyValidation());
      setIsEditMode(false);
      setEditingKey(null);
      setCurrentStep(1);
      // Clear any pending debounce timeout
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
        setDebounceTimeout(null);
      }
    }
  }, [isAddModalOpen, dispatch, debounceTimeout]);

  useEffect(() => {
    if (formState.success && !formState.loading) {
      resetForm();
      setIsAddModalOpen(false);
    }
  }, [formState.success, formState.loading]);

  const appliesToOptions = [
    {
      value: "products",
      label: "Products",
      icon: <Package className="h-4 w-4" />,
      description: "Add custom fields to products",
    },
    {
      value: "collections",
      label: "Collections",
      icon: <FolderOpen className="h-4 w-4" />,
      description: "Add custom fields to collections",
    },
    {
      value: "coupons",
      label: "Coupons",
      icon: <Tag className="h-4 w-4" />,
      description: "Add custom fields to coupons",
    },
  ];

  const metaFieldTypes = [
    {
      value: "text",
      label: "Text",
      icon: <Type className="h-4 w-4" />,
      description: "Text input field",
    },
    {
      value: "number",
      label: "Number",
      icon: <Hash className="h-4 w-4" />,
      description: "Numeric input field",
    },
    {
      value: "select",
      label: "Select",
      icon: <List className="h-4 w-4" />,
      description: "Dropdown selection with predefined values",
    },
    {
      value: "email",
      label: "Email",
      icon: <Mail className="h-4 w-4" />,
      description: "Email address field",
    },
    {
      value: "url",
      label: "URL",
      icon: <Globe className="h-4 w-4" />,
      description: "Website URL field",
    },
    {
      value: "link",
      label: "Link",
      icon: <Link className="h-4 w-4" />,
      description: "Internal or external link field",
    },
  ];

  const getTypeIcon = (type: MetafieldType | string | undefined) => {
    const typeConfig: Record<string, { icon: React.ReactNode; color: string }> =
      {
        text: { icon: <Type className="h-4 w-4" />, color: "text-blue-600" },
        number: { icon: <Hash className="h-4 w-4" />, color: "text-green-600" },
        select: {
          icon: <List className="h-4 w-4" />,
          color: "text-purple-600",
        },
        email: { icon: <Mail className="h-4 w-4" />, color: "text-orange-600" },
        url: { icon: <Globe className="h-4 w-4" />, color: "text-cyan-600" },
        link: { icon: <Link className="h-4 w-4" />, color: "text-indigo-600" },
      };

    const cfg = type ? typeConfig[type] : undefined;
    const safeCfg = cfg ?? {
      icon: <Type className="h-4 w-4" />,
      color: "text-gray-600",
    };

    return <div className={`${safeCfg.color}`}>{safeCfg.icon}</div>;
  };

  const getTypeBadge = (type: MetafieldType | string | undefined) => {
    const typeColors: Record<string, string> = {
      text: "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
      number: "bg-green-500/20 text-green-600 hover:bg-green-500/20",
      select: "bg-purple-500/20 text-purple-600 hover:bg-purple-500/20",
      email: "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20",
      url: "bg-cyan-500/20 text-cyan-600 hover:bg-cyan-500/20",
      link: "bg-indigo-500/20 text-indigo-600 hover:bg-indigo-500/20",
    };

    const cls = type ? typeColors[type] : undefined;

    return (
      <Badge className={cls ?? "bg-gray-200 text-gray-600"}>
        {type ? type.charAt(0).toUpperCase() + type.slice(1) : "N/A"}
      </Badge>
    );
  };

  const getAppliesToBadge = (
    appliedTo: MetafieldAppliedTo | string | undefined,
  ) => {
    const appliesToColors: Record<string, string> = {
      products: "bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/20",
      collections: "bg-amber-500/20 text-amber-600 hover:bg-amber-500/20",
      coupons: "bg-rose-500/20 text-rose-600 hover:bg-rose-500/20",
    };

    const cls = appliedTo ? appliesToColors[appliedTo] : undefined;

    return (
      <Badge className={cls ?? "bg-gray-200 text-gray-600"}>
        {appliedTo
          ? appliedTo.charAt(0).toUpperCase() + appliedTo.slice(1)
          : "N/A"}
      </Badge>
    );
  };

  const debouncedCheckKeyUniqueness = useCallback(
    (key: string, appliedTo: string) => {
      // Clear previous timeout
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      // Only check if key is not empty and we have both key and applied_to
      if (key.trim() && appliedTo) {
        const timeout = setTimeout(() => {
          dispatch(
            checkMetafieldKeyRequest({
              key: key.trim(),
              applied_to: appliedTo,
            }),
          );
        }, 500); // 500ms debounce

        setDebounceTimeout(timeout);
      } else {
        // Reset validation state if key is empty
        dispatch(resetKeyValidation());
      }
    },
    [dispatch, debounceTimeout],
  );

  const handleKeyChange = (newKey: string) => {
    // Auto-convert to slug format as user types
    const slugKey = newKey
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/[^a-z0-9-]/g, "") // Remove non-alphanumeric characters except hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens

    setAddForm((prev) => ({ ...prev, key: slugKey }));

    // Only check uniqueness when creating new metafields (not editing)
    if (!isEditMode && addForm.applied_to && slugKey) {
      debouncedCheckKeyUniqueness(slugKey, addForm.applied_to);
    }
  };

  const getKeyValidationStatus = () => {
    if (isEditMode || !addForm.key.trim()) return null;

    // First check slug format
    if (!isValidSlug(addForm.key.trim())) {
      return {
        icon: <XCircle className="h-4 w-4 text-red-600" />,
        message:
          "Key must be in slug format (lowercase letters, numbers, and hyphens only, no spaces)",
        color: "text-red-600",
      };
    }

    if (keyValidation.loading) {
      return {
        icon: (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ),
        message: "Checking availability...",
        color: "text-muted-foreground",
      };
    }

    if (keyValidation.lastCheckedKey === addForm.key.trim()) {
      if (keyValidation.isUnique === true) {
        return {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          message: "This key is available",
          color: "text-green-600",
        };
      } else if (keyValidation.isUnique === false) {
        return {
          icon: <XCircle className="h-4 w-4 text-red-600" />,
          message: "This key is already in use",
          color: "text-red-600",
        };
      }
    }

    if (keyValidation.error) {
      return {
        icon: <XCircle className="h-4 w-4 text-orange-600" />,
        message: "Unable to check availability",
        color: "text-orange-600",
      };
    }

    return null;
  };

  const isKeyValid = () => {
    if (isEditMode) return true;
    if (!addForm.key.trim()) return false;

    // Check slug format first
    if (!isValidSlug(addForm.key.trim())) return false;

    // If we're still loading or haven't checked this key yet, consider it invalid
    if (
      keyValidation.loading ||
      keyValidation.lastCheckedKey !== addForm.key.trim()
    ) {
      return false;
    }

    return keyValidation.isUnique === true;
  };

  const handleAddMetaField = () => {
    // For new metafields, ensure the key is valid and unique
    if (!isEditMode && !isKeyValid()) {
      return;
    }

    if (addForm.key && addForm.type && addForm.applied_to) {
      const metafieldData = {
        key: addForm.key,
        type: addForm.type as MetafieldType,
        applied_to: addForm.applied_to as MetafieldAppliedTo,
        accepted_range:
          addForm.accepted_range.length > 0
            ? addForm.accepted_range
            : undefined,
        accepted_values:
          addForm.accepted_values.length > 0
            ? addForm.accepted_values
            : undefined,
      };

      if (isEditMode && editingKey) {
        dispatch(
          updateMetafieldRequest({
            key: editingKey,
            data: metafieldData as UpdateMetafieldDto,
          }),
        );
      } else {
        dispatch(createMetafieldRequest(metafieldData as CreateMetafieldDto));
      }
    }
  };

  const resetForm = () => {
    setAddForm({
      key: "",
      type: "",
      accepted_range: [],
      accepted_values: [],
      applied_to: "",
    });
    setCurrentStep(1);
    dispatch(resetKeyValidation());
    // Clear any pending debounce timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
      setDebounceTimeout(null);
    }
  };

  const handleEditMetafield = (metafield: Metafield) => {
    setAddForm({
      key: metafield.key,
      type: metafield.type,
      accepted_range: metafield.accepted_range || [],
      accepted_values: metafield.accepted_values || [],
      applied_to: metafield.applied_to || ("products" as MetafieldAppliedTo),
    });
    setIsEditMode(true);
    setEditingKey(metafield.key);
    setCurrentStep(3); // Skip to form step for editing
    setIsAddModalOpen(true);
  };

  const handleRemoveMetaField = async (id: string) => {
    const result = await confirm({
      title: "Delete Metafield",
      message:
        "Are you sure you want to delete this metafield? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "destructive",
    });

    if (result.confirmed) {
      dispatch(deleteMetafieldRequest({ id }));
    }
  };

  const renderValidationInfo = (metaField: Metafield) => {
    if (!metaField.accepted_range && !metaField.accepted_values) return null;

    if (metaField.accepted_values && metaField.accepted_values.length > 0) {
      return (
        <div className="text-xs text-muted-foreground">
          Values: {metaField.accepted_values.join(", ")}
        </div>
      );
    }

    if (metaField.accepted_range && metaField.accepted_range.length > 0) {
      const [min, max] = metaField.accepted_range;
      if (metaField.type === "text") {
        return (
          <div className="text-xs text-muted-foreground">
            Length: {min ? `${min} min` : ""} {max ? `${max} max` : ""}
          </div>
        );
      } else if (metaField.type === "number") {
        return (
          <div className="text-xs text-muted-foreground">
            Range: {min ?? "No min"} - {max ?? "No max"}
          </div>
        );
      }
    }

    return null;
  };

  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return !!addForm.applied_to;
      case 2:
        return !!addForm.type;
      case 3:
        return !!addForm.key;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">
                Where will this metafield be used?
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Select the type of content this metafield will be applied to.
              </p>
            </div>
            <div className="grid gap-3">
              {appliesToOptions.map((option) => (
                <div
                  key={option.value}
                  className={`p-4 border rounded-lg cursor-pointer transition-all hover:bg-accent ${
                    addForm.applied_to === option.value
                      ? "border-primary bg-primary/5"
                      : "border-border"
                  }`}
                  onClick={() => {
                    const newAppliedTo = option.value as MetafieldAppliedTo;
                    setAddForm((prev) => ({
                      ...prev,
                      applied_to: newAppliedTo,
                    }));

                    // If we already have a key, recheck its uniqueness with the new scope
                    if (!isEditMode && addForm.key.trim()) {
                      debouncedCheckKeyUniqueness(addForm.key, newAppliedTo);
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    {option.icon}
                    <div>
                      <h3 className="font-medium">{option.label}</h3>
                      <p className="text-sm text-muted-foreground">
                        {option.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">
                What type of metafield do you want to create?
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Choose the data type that best fits your needs.
              </p>
            </div>
            <div className="grid gap-3">
              {metaFieldTypes.map((type) => (
                <div
                  key={type.value}
                  className={`p-4 border rounded-lg cursor-pointer transition-all hover:bg-accent ${
                    addForm.type === type.value
                      ? "border-primary bg-primary/5"
                      : "border-border"
                  }`}
                  onClick={() => {
                    setAddForm((prev) => ({
                      ...prev,
                      type: type.value as MetafieldType,
                      accepted_values: [], // Reset values when type changes
                      accepted_range: [], // Reset range when type changes
                    }));

                    // If we already have a key and applied_to, recheck uniqueness
                    if (
                      !isEditMode &&
                      addForm.key.trim() &&
                      addForm.applied_to
                    ) {
                      debouncedCheckKeyUniqueness(
                        addForm.key,
                        addForm.applied_to,
                      );
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    {type.icon}
                    <div>
                      <h3 className="font-medium">{type.label}</h3>
                      <p className="text-sm text-muted-foreground">
                        {type.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-semibold">
                Configure your metafield
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Set up the details and validation rules for your {addForm.type}{" "}
                metafield.
              </p>
            </div>

            {/* Summary of previous choices */}
            {!isEditMode && (
              <div className="p-4 bg-muted/50 rounded-lg border">
                <h4 className="font-medium mb-3">
                  Summary of your selections:
                </h4>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Applies to:
                    </span>
                    <div className="flex items-center gap-1">
                      {
                        appliesToOptions.find(
                          (option) => option.value === addForm.applied_to,
                        )?.icon
                      }
                      <span className="font-medium capitalize">
                        {addForm.applied_to}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Type:</span>
                    <div className="flex items-center gap-1">
                      {
                        metaFieldTypes.find(
                          (type) => type.value === addForm.type,
                        )?.icon
                      }
                      <span className="font-medium capitalize">
                        {addForm.type}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {formState.error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">
                  {formState.error.message}
                </p>
              </div>
            )}

            {/* Meta Field Key */}
            <div className="space-y-2">
              <StandardFormInput
                label="Metafield Key"
                placeholder="e.g., brand-name, color-code, material-type"
                value={addForm.key}
                onChange={(e) => handleKeyChange(e.target.value)}
                disabled={isEditMode}
                helperText={
                  isEditMode
                    ? "Key cannot be changed when editing an existing metafield."
                    : "Use lowercase letters, numbers, and hyphens only. No spaces allowed (e.g., 'brand-name', 'color-code'). This key must be unique and will be used to identify this metafield."
                }
              />

              {/* Key validation status */}
              {(() => {
                const validationStatus = getKeyValidationStatus();
                if (!validationStatus) return null;

                return (
                  <div
                    className={`flex items-center gap-2 text-sm ${validationStatus.color}`}
                  >
                    {validationStatus.icon}
                    <span>{validationStatus.message}</span>
                  </div>
                );
              })()}
            </div>

            {/* Type-specific configuration */}
            {addForm.type === "select" && (
              <div className="space-y-2">
                <Label>Select Options</Label>
                <StandardFormTagInput
                  label=""
                  tags={addForm.accepted_values}
                  onTagsChange={(values) =>
                    setAddForm((prev) => ({ ...prev, accepted_values: values }))
                  }
                  placeholder="Enter option and press Enter"
                  helperText="Add the options that users can select from."
                />
              </div>
            )}

            {/* Range validation for text (length) */}
            {addForm.type === "text" && (
              <div className="grid grid-cols-2 gap-4">
                <StandardFormInput
                  label="Minimum Length (optional)"
                  type="number"
                  placeholder="0"
                  value={addForm.accepted_range[0]?.toString() || ""}
                  onChange={(e) => {
                    const value = e.target.value
                      ? parseInt(e.target.value)
                      : undefined;
                    setAddForm((prev) => ({
                      ...prev,
                      accepted_range: [
                        value || 0,
                        prev.accepted_range[1] || 0,
                      ].filter(Boolean),
                    }));
                  }}
                />
                <StandardFormInput
                  label="Maximum Length (optional)"
                  type="number"
                  placeholder="255"
                  value={addForm.accepted_range[1]?.toString() || ""}
                  onChange={(e) => {
                    const value = e.target.value
                      ? parseInt(e.target.value)
                      : undefined;
                    setAddForm((prev) => ({
                      ...prev,
                      accepted_range: [
                        prev.accepted_range[0] || 0,
                        value || 0,
                      ].filter(Boolean),
                    }));
                  }}
                />
              </div>
            )}

            {/* Range validation for numbers */}
            {addForm.type === "number" && (
              <div className="grid grid-cols-2 gap-4">
                <StandardFormInput
                  label="Minimum Value (optional)"
                  type="number"
                  placeholder="0"
                  value={addForm.accepted_range[0]?.toString() || ""}
                  onChange={(e) => {
                    const value = e.target.value
                      ? parseFloat(e.target.value)
                      : undefined;
                    setAddForm((prev) => ({
                      ...prev,
                      accepted_range: [
                        value || 0,
                        prev.accepted_range[1] || 0,
                      ].filter(Boolean),
                    }));
                  }}
                />
                <StandardFormInput
                  label="Maximum Value (optional)"
                  type="number"
                  placeholder="1000"
                  value={addForm.accepted_range[1]?.toString() || ""}
                  onChange={(e) => {
                    const value = e.target.value
                      ? parseFloat(e.target.value)
                      : undefined;
                    setAddForm((prev) => ({
                      ...prev,
                      accepted_range: [
                        prev.accepted_range[0] || 0,
                        value || 0,
                      ].filter(Boolean),
                    }));
                  }}
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const columns: ColumnDef<Metafield>[] = [
    {
      accessorKey: "key",
      header: "Meta Field",
      size: 350,
      cell: ({ row }) => {
        const metaField = row.original;
        return (
          <div className="flex items-center gap-3 min-w-[300px]">
            <div className="h-10 w-10 bg-muted/50 rounded-lg flex items-center justify-center">
              {getTypeIcon(metaField.type)}
            </div>
            <div>
              <p className="font-medium text-foreground">{metaField.key}</p>
              <div className="text-xs text-muted-foreground">
                Field type: {metaField.type}
              </div>
              {renderValidationInfo(metaField)}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row }) => {
        return getTypeBadge(row.original.type);
      },
    },
    {
      accessorKey: "applied_to",
      header: "Applied To",
      cell: ({ row }) => {
        const appliedTo = row.original.applied_to;
        return getAppliesToBadge(appliedTo);
      },
    },
    {
      accessorKey: "updated_at",
      header: "Last Updated",
      size: 140,
      cell: ({ row }) => {
        const date = new Date(row.original.updated_at);
        return (
          <div className="text-sm text-muted-foreground">
            {date.toLocaleDateString()}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 120,
      cell: ({ row }) => {
        const metaField = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleEditMetafield(metaField)}
            >
              <Edit3 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
              onClick={() => handleRemoveMetaField(metaField.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const handleAddNewMetaField = () => {
    // Reset form and ensure we're in add mode
    resetForm();
    setIsEditMode(false);
    setEditingKey(null);
    setCurrentStep(1);
    dispatch(resetFormState());
    dispatch(resetKeyValidation());
    setIsAddModalOpen(true);
  };

  const handleRefresh = () => {
    fetchMetaFields();
  };

  // Create action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: (
        <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
      ),
      onClick: handleRefresh,
      variant: "outline",
      disabled: loading,
    },
    {
      label: "Add Meta Field",
      icon: <Plus className="h-3 w-3" />,
      onClick: handleAddNewMetaField,
      variant: "primary",
    },
  ];

  return (
    <div>
      <TableContainer>
        <div className="bg-secondary-background rounded-lg p-4">
          <DataView
            title="Meta Fields"
            description="Define custom fields for products, collections, and coupons"
            columns={columns}
            data={metaFields}
            pagination={pagination}
            pageIndex={pageIndex}
            onPageChange={setPageIndex}
            loading={loading}
            emptyStateType="general"
            actionButtons={actionButtons}
          />
        </div>
      </TableContainer>

      {/* Add Meta Field Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto bg-secondary-background">
          <DialogHeader>
            <DialogTitle>
              {isEditMode
                ? "Edit Meta Field"
                : `Add New Meta Field ${!isEditMode ? `- Step ${currentStep} of 3` : ""}`}
            </DialogTitle>
            <DialogDescription>
              {isEditMode
                ? "Update the metafield configuration."
                : currentStep === 1
                  ? "Choose where this metafield will be used."
                  : currentStep === 2
                    ? "Select the type of metafield you want to create."
                    : "Configure your metafield details and validation rules."}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">{renderStepContent()}</div>

          <DialogFooter>
            <div className="flex justify-between w-full">
              <div className="flex gap-2">
                {!isEditMode && currentStep > 1 && (
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(currentStep - 1)}
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    resetForm();
                    setIsAddModalOpen(false);
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>

              <div>
                {!isEditMode && currentStep < 3 ? (
                  <Button
                    onClick={() => setCurrentStep(currentStep + 1)}
                    disabled={!canGoNext()}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleAddMetaField}
                    disabled={
                      !addForm.key ||
                      !addForm.type ||
                      !addForm.applied_to ||
                      formState.loading ||
                      (!isEditMode && !isKeyValid()) ||
                      (!isEditMode && keyValidation.loading)
                    }
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {formState.loading
                      ? "Saving..."
                      : isEditMode
                        ? "Update Meta Field"
                        : "Create Meta Field"}
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetaFields;
